import type { IconDefinition } from "@fortawesome/fontawesome-common-types"
import {
  faChevronRight,
  faCircleInfo,
  faSnowflakes,
  faTicket,
} from "@fortawesome/pro-regular-svg-icons"
import { Box, Link, Stack, Typography, useMediaQuery } from "@mui/material"
import { styled } from "@mui/material/styles"
import { SkipassSolid } from "@skistarab/icons"
import theme, { FaIcon } from "@skistarab/mui-theme"

export type BookingBannerData = {
  icon: string
  theme: string
  header: string
  body: string
  linkName?: string
  linkUrl?: string
}

type BannerStyle = { bg: string; iconColor: string }

const paletteStyle = (key: "summer" | "winter"): BannerStyle => {
  const shades = theme.palette[key] as unknown as Record<string, string>
  return { bg: shades[50], iconColor: shades[300] }
}

const THEME_STYLE: Record<string, BannerStyle> = {
  Neutral: { bg: "#F1E5E4", iconColor: "#D5AEAA" },
  Sommar: paletteStyle("summer"),
  Vinter: paletteStyle("winter"),
}
const DEFAULT_STYLE = THEME_STYLE.Neutral

// Admin icon name -> icon. FontAwesome definitions render via <FaIcon>; the
// SkipassSolid SVG component is the fallback when no icon is provided and
// renders via <Box component={...}>.
type IconComponent = typeof SkipassSolid
const ICON_MAP: Record<string, IconDefinition> = {
  "circle-info": faCircleInfo,
  ticket: faTicket,
  snowflakes: faSnowflakes,
}
const DEFAULT_ICON: IconDefinition | IconComponent = SkipassSolid

const isIconComponent = (
  entry: IconDefinition | IconComponent
): entry is IconComponent => typeof entry === "function"

const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bg",
})<{ bg: string }>(({ bg }) => ({
  backgroundColor: bg,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
}))

const BookingBanner = ({ data }: { data: BookingBannerData }) => {
  const isTablet = useMediaQuery(theme.breakpoints.up("md"))

  const { bg, iconColor } = THEME_STYLE[data.theme] ?? DEFAULT_STYLE

  const iconEntry = ICON_MAP[data.icon] ?? DEFAULT_ICON
  const icon = isIconComponent(iconEntry) ? (
    <Box
      component={iconEntry}
      sx={{
        color: iconColor,
        fontSize: isTablet ? 40 : 24,
        width: "1em",
        height: "1em",
      }}
    />
  ) : (
    <FaIcon
      icon={iconEntry}
      sx={{ color: iconColor, fontSize: isTablet ? 40 : 24 }}
    />
  )

  const headline = (
    <Typography variant={isTablet ? "body1" : "body2"} fontWeight="bold">
      {data.header}
    </Typography>
  )

  const body = (
    <Typography variant={isTablet ? "body2" : "body3"}>{data.body}</Typography>
  )

  const link = data.linkName && data.linkUrl && (
    <Link
      href={data.linkUrl}
      target="_blank"
      rel="noopener noreferrer"
      color="textPrimary"
      underline="none"
      variant="body3"
      fontWeight={600}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        whiteSpace: "nowrap",
        fontSize: "0.75rem",
      }}
    >
      {data.linkName}
      <FaIcon icon={faChevronRight} fontSize="inherit" />
    </Link>
  )

  return (
    <Container bg={bg}>
      {isTablet ? (
        <Stack direction="row" alignItems="center">
          {icon}
          <Box flexGrow={1} ml={2} mr={data.linkName && data.linkUrl ? 3 : 0}>
            {headline}
            {body}
          </Box>
          {link}
        </Stack>
      ) : (
        <Stack spacing={0.5} alignItems="flex-start">
          {icon}
          {headline}
          {body}
          {link}
        </Stack>
      )}
    </Container>
  )
}

export default BookingBanner
